import { Entity, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Column } from '../../decorators';
import { OrderStatus } from '../../enums';
import { formatEnumAsSelectOptions } from '../../utils';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { ClientAddressItemEntity } from './client-address-item.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientItemEntity } from './client-item.entity';
import { type ClientBillingMode, ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { MSmPaymentTransactionOrderEntity } from './m-sm-payment-transaction-order.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { OrderFileEntity } from './order-file.entity';
import { OrderNoteEntity } from './order-note.entity';
import { PublicUserEntity } from './public-user.entity';
import { ShippingMethodEntity } from './shipping-method.entity';
import { TaxEntity } from './tax.entity';
import { TermsCodeEntity } from './terms-code.entity';
import { TripStopEntity } from './trip-stop.entity';
import { UserEntity } from './user.entity';

/** PD-17102 — what became of a quote. */
export const QUOTE_OUTCOMES = ['open', 'won', 'lost', 'expired'] as const;

export type QuoteOutcome = (typeof QUOTE_OUTCOMES)[number];

/**
 * PD-17316 — how an order or quote came to be on its job, or off it:
 *  - `auto`: the job auto-link put it there (its ERP job contract or its job
 *    name matched one of the customer's jobs);
 *  - `user`: a rep linked it to a job or took it off one.
 * An order with no job and no source is one the auto-link may still place.
 * Any source keeps the auto-link away from the order, so a rep's link or
 * unlink is never undone by it.
 */
export const JOB_LINK_SOURCES = ['auto', 'user'] as const;

export type JobLinkSource = (typeof JOB_LINK_SOURCES)[number];

@Index('externalId_fk_company__id', ['externalId', 'fkCompanyId'], {
	unique: true,
})
@Index('fk_company__id', ['fkCompanyId'])
@Index('order__fk_job__id', ['fkJobId'])
@Index('order__jobContractExternalId', ['jobContractExternalId'])
@Index('order__fk_warehouse__id', ['fkWarehouseId'])
@Index('fk_public_user__id_override', ['fkPublicUserIdOverride'])
@Index('fk_public_user__id_submitter', ['fkPublicUserIdSubmitter'])
@Index('fk_tax__id', ['fkTaxId'])
@Index('fk_user__id_override', ['fkUserIdOverride'])
@Index('fk_user__id_submitter', ['fkUserIdSubmitter'])
@Index('fx_order_fulltext', ['externalId', 'customerPO'], { fulltext: true })
// PD-14788: covering index for the invoice keyword search's customer-PO
// id-resolution query (DERIVED_KEYWORD_ID_QUERIES in typeormFilterBuilder.ts,
// which names it in a USE INDEX hint). The LIKE has a leading wildcard so no
// seek on customerPO is possible — the win is ref-access on the
// _deleted_date IS NULL prefix with customerPO evaluated inside this narrow
// covering index (both columns + implicit PK) instead of a row lookup per
// live order: ~0.1s vs ~0.8s on pharmsource_staging. The hint is required —
// the 5.7 optimizer prefers the non-covering _deleted_date ref on
// idx_order_deleted_company_date on its own, whichever column order this
// index uses.
@Index('idx_order_deleted_customer_po', ['deletedDate', 'customerPO'])
// PD-17593: Pharmsource keeps its sales order number (SO646368166) in
// externalId2. The Orders list's order-number search (order-number-keyword.ts
// in crm-api) reads this column and the primary key straight from this narrow
// index instead of reading every order row, and an exact or prefix lookup on
// the order number can seek it.
@Index('idx_order_external_id2', ['externalId2'])
@Index('m_sm_payment_fk_m_payment_method__id', ['mSmPaymentFkMPaymentMethodId'])
@Index('order__fk_client__id_fk', ['fkClientId'])
@Index('idx_order_billing_account', ['fkClientIdBilling'], {})
@Index('order__fk_client__id_delivery_fk', ['fkClientIdDelivery'])
@Index('order__fk_client_address__id_billing_fk', ['fkClientAddressIdBilling'])
@Index('order__fk_client_address__id_shipping_fk', [
	'fkClientAddressIdShipping',
])
@Index('order__fk_user__id_fk', ['fkUserId'])
@Index('order_externalId_fk_company__id__deleted_userId', [
	'externalId',
	'fkCompanyId',
	'deletedUserId',
])
@Index('idx_order_company_status_deleted_date', [
	'fkCompanyId',
	'deletedDate',
	'status',
	'date',
])
@Index('idx_order_deleted_company_date', ['deletedDate', 'fkCompanyId', 'date'])
@Index('idx_order_client_status_deleted', [
	'fkClientId',
	'status',
	'deletedDate',
])
@Index('idx_order_delivery_date', ['deliveryDate'])
// PD-16802: the Quote and Order Activity report asks, for each order in its
// range, whether a Workd quote was converted into it.
@Index('idx_order_quote_converted_to', ['quoteConvertedToOrderId'])
// PD-14959 — the global search catch-up finds the orders changed since its
// last run by this column every five minutes (ERP-imported orders reach
// search only this way). Without it each run scans the whole table, about
// 650,000 rows on the largest tenant. Same shape as invoice's PD-17142 index.
@Index('idx_order_modified_date', ['modifiedDate'], {})
@Entity('order')
export class OrderEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('datetime', { name: 'date', nullable: true })
	public date!: Date | null;

	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	/**
	 * PD-15849 — the billing account this order belongs to: the ordering
	 * client, or its parent when the client is billed with parent. Stamped
	 * at creation from the client's resolved billing account and never
	 * re-derived afterwards, because flipping "Bill with parent" is
	 * non-retroactive — only an explicit move restates existing rows.
	 *
	 * Declared NULLABLE deliberately. Our database sync is additive only
	 * (it never ALTERs an existing column), so a NOT NULL column could not
	 * be added to a table that already has rows. The PD-15849 migration
	 * backfills `fk_client__id_billing = _fk_client__id` for every existing
	 * row, but the column stays nullable so a tenant whose migration has
	 * not run yet still reads correctly: NULL means "billed to
	 * `_fk_client__id`". Read it through `billingAccountOf`
	 * (`libs/shared/src/utils/billing-account.ts`), never directly.
	 */
	@Column('int', {
		name: 'fk_client__id_billing',
		nullable: true,
		unsigned: true,
		comment:
			'Billing account for this order (the parent when billed with parent). NULL = billed to _fk_client__id.',
	})
	public fkClientIdBilling!: number | null;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('int', { name: 'numberOfLines', nullable: true })
	public numberOfLines!: number | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	// PD-14375 — the pre-promo freight base the recalc pipeline computed
	// (shipping-method effective price incl. free-shipping threshold +
	// surcharges, or a rep's manual entry). `freight` holds the FINAL
	// charged value (base minus any shipping-promo discount); this column
	// anchors the promo math so re-runs discount the base rather than
	// compounding on an already-discounted `freight`, and a lapsed
	// shipping promo restores `freight` back to the base. NULL on orders
	// that predate the column or never went through the recalc pipeline —
	// consumers fall back to `freight`.
	@Column('decimal', {
		name: 'freightBase',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freightBase!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	@Column(
		'varchar',
		{
			name: 'status',
			nullable: true,
			length: 255,
			default: () => `'${OrderStatus.UNSUBMITTED}'`,
		},
		{
			selectOptions: formatEnumAsSelectOptions(OrderStatus),
		},
	)
	public status!: OrderStatus | null;

	@Column('varchar', { name: 'customerPO', nullable: true, length: 255 })
	public customerPO!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'alertStatus', nullable: true, length: 255 })
	public alertStatus!: string | null;

	@Column({ type: 'boolean', name: 'canCancel', width: 1, default: () => "'0'" })
	public canCancel!: boolean;

	@Column('varchar', { name: 'origin', length: 255, default: () => "'2'" })
	public origin!: string;

	@Column('varchar', { name: 'shipVia', nullable: true, length: 255 })
	public shipVia!: string | null;

	@Column('text', { name: 'statusText', nullable: true })
	public statusText!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 255 })
	public externalId2!: string | null;

	@Column('date', { name: 'shippedByDate', nullable: true })
	public shippedByDate!: string | null;

	/**
	 * PD-16114 — the day the order is to be delivered ("deliver on"), a local
	 * calendar date. Drives the Schedule page and the trip builder's "due by the
	 * trip date" rule. NULL means "whenever it next goes on a truck", which is
	 * how every order behaved before. Not `shippedByDate`: that is an ERP field
	 * distribution never reads.
	 */
	@Column('date', { name: 'deliveryDate', nullable: true })
	public deliveryDate!: string | null;

	/** PD-16114 — optional delivery time window on `deliveryDate` ("HH:MM:SS"). */
	@Column('time', { name: 'deliveryWindowStart', nullable: true })
	public deliveryWindowStart!: string | null;

	@Column('time', { name: 'deliveryWindowEnd', nullable: true })
	public deliveryWindowEnd!: string | null;

	/**
	 * PD-16114 — the other half of an event rental. An event is two orders, one
	 * per date (delivery day and pick-up day), because an order rides exactly
	 * one trip stop and is invoiced there. Each points at the other, so a date
	 * change or a cancellation reaches both.
	 */
	@Column('int', {
		name: 'fk_order__id_related',
		nullable: true,
		unsigned: true,
	})
	public fkOrderIdRelated!: number | null;

	@Column('int', {
		name: 'fk_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdSubmitter!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdBilling!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_shipping',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdShipping!: number | null;

	// PD-15423 — cross-account delivery: when set, the order is delivered to
	// THIS client's site (fkClientAddressIdShipping then points at one of the
	// delivery client's addresses) while the order and invoice stay on
	// fkClientId. Null = normal delivery to the ordering client.
	@Column('int', {
		name: 'fk_client__id_delivery',
		nullable: true,
		unsigned: true,
	})
	public fkClientIdDelivery!: number | null;

	@Column('date', { name: 'expirationDate', nullable: true })
	public expirationDate!: string | null;

	@Column('int', {
		name: 'fk_public_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public fkPublicUserIdSubmitter!: number | null;

	@Column('int', {
		name: 'fk_company__id',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyId!: number;

	@Column('longtext', { name: 'approvalRequiredReason', nullable: true })
	public approvalRequiredReason!: string | null;

	@Column('datetime', { name: 'overrideDateTime', nullable: true })
	public overrideDateTime!: Date | null;

	@Column('int', {
		name: 'fk_user__id_override',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdOverride!: number | null;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('int', {
		name: 'm_sm_payment_fk_m_payment_method__id',
		nullable: true,
		unsigned: true,
	})
	public mSmPaymentFkMPaymentMethodId!: number | null;

	/**
	 * PD-15847 — the payment method TYPE this order is to be settled with.
	 *
	 * The FK above only names a saved (vaulted) row, so a Company Credit,
	 * Cash or Check order was previously indistinguishable from one whose
	 * method was simply never chosen. Stamped from the client's resolved
	 * default at creation and overwritten by an explicit choice at submit.
	 *
	 * Invariant: when `mSmPaymentFkMPaymentMethodId` is set, this equals
	 * that row's `mPaymentType`. NULL on pre-existing orders — readers
	 * derive from the FK row when present, else treat it as unknown.
	 */
	@Column('varchar', {
		name: 'paymentMethodType',
		nullable: true,
		length: 64,
		comment:
			'Payment method type for this order (PaymentMethodTypeEnum value). Matches the saved method row when one is linked.',
	})
	public paymentMethodType!: string | null;

	/**
	 * PD-15848 — a per-ORDER terms override (QuickBooks-style).
	 *
	 * The client's `fkTermsCodeId` remains the default; this only names a
	 * different terms code for this one order, and
	 * `InvoicesService._resolveEffectiveTermsCode` prefers it over the
	 * client's when the invoice is raised. NULL — every existing order —
	 * means "use the client's terms", which is exactly what happened
	 * before this column existed.
	 */
	@Column('int', {
		name: 'fk_terms_code__id',
		nullable: true,
		unsigned: true,
		comment:
			'Per-order payment terms override. NULL = use the client terms code.',
	})
	public fkTermsCodeId!: number | null;

	/**
	 * PD-16900 — a per-ORDER billing override, the billing-mode sibling of
	 * `fkTermsCodeId` above. The customer's (billing account's) mode stays
	 * the default; this bills one order differently — an event paid up
	 * front, say, for a customer otherwise charged on delivery.
	 *
	 * It decides what the card is asked for when the order is placed and
	 * whether the delivery charges it, so it is only writable while the
	 * order is a cart, and it is ignored entirely when the tenant's
	 * `payments.billing_modes` setting is off. Read it through
	 * `resolveOrderBillingMode`, never directly. NULL — every existing
	 * order — means "use the customer's mode".
	 */
	@Column('varchar', {
		name: 'billingModeOverride',
		nullable: true,
		length: 32,
		comment:
			"Per-order billing mode override: pay_at_order, charge_on_delivery or bill_on_terms. NULL = use the customer's billing mode.",
	})
	public billingModeOverride!: ClientBillingMode | null;

	@Column('varchar', {
		name: 'm_sm_payment_fee_unit',
		nullable: true,
		length: 32,
	})
	public mSmPaymentFeeUnit!: string | null;

	@Column('decimal', {
		name: 'm_sm_payment_fee_amount',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeAmount!: string;

	/**
	 * PD-15143 — the processing fee on this order, in DOLLARS, stored rather
	 * than derived. `total` includes it.
	 *
	 * The order needs its own figure and cannot lean on the invoice: on the
	 * charge-at-submit flows the money moves before any invoice exists. Same
	 * basis rule as the invoice — projected from the stamped rate until
	 * payments post, then the sum of those transactions' actual fees.
	 */
	@Column('decimal', {
		name: 'm_sm_payment_fee_total',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public mSmPaymentFeeTotal!: string;

	@Column('decimal', {
		name: 'shipCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public shipCost!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('int', { name: 'fk_tax__id', nullable: true, unsigned: true })
	public fkTaxId!: number | null;

	@Column('decimal', {
		name: 'taxRate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public taxRate!: string | null;

	@Column('int', {
		name: 'fk_public_user__id_override',
		nullable: true,
		unsigned: true,
	})
	public fkPublicUserIdOverride!: number | null;

	@Column('varchar', { name: 'jobNumber', nullable: true, length: 64 })
	public jobNumber!: string | null;

	/**
	 * PD-17093 — the job this order or quote belongs to (tenants with the
	 * `jobs` capability). Set in Workd by a rep; `jobNumber` above stays the raw
	 * ERP value and is never rewritten from the job. Nullable, so SM1's order
	 * screens and imports are unaffected.
	 */
	@Column('int', { name: 'fk_job__id', nullable: true, unsigned: true })
	public fkJobId!: number | null;

	/**
	 * PD-17316 — the ERP job contract the order was placed under, as the
	 * external id of the Workd price contract imported for that contract
	 * (Prophet 21: `JOBCONTRACT_<job_price_hdr_uid>`). Written by the ERP
	 * import; the job auto-link puts the order on the customer's job linked to
	 * that price contract.
	 *
	 * SM1 exposure (this and `jobLinkSource`): none. Both are nullable with no
	 * default and the legacy CRM never names them on `order`.
	 */
	@Column('varchar', { name: 'jobContractExternalId', nullable: true, length: 64 })
	public jobContractExternalId!: string | null;

	/** PD-17316 — who put the order on its job, or took it off (JOB_LINK_SOURCES). */
	@Column('varchar', { name: 'jobLinkSource', nullable: true, length: 16 })
	public jobLinkSource!: JobLinkSource | null;

	/**
	 * PD-17225 — the warehouse the order is booked to (for Prophet 21, the
	 * order's sales location). Order entry records the warehouse it takes stock
	 * from; the ERP import fills it for imported orders. Column and index only,
	 * no database foreign key, for the same reason as invoice.fk_warehouse__id.
	 */
	@Column('int', { name: 'fk_warehouse__id', nullable: true, unsigned: true })
	public fkWarehouseId!: number | null;

	/**
	 * PD-17102 — a quote's outcome: null or 'open' while it is open, 'won' when
	 * it became an order (set by the quote-to-cart conversion, which then
	 * soft-deletes the quote row), 'lost' with a reason, 'expired' after its
	 * expiration date (nightly). A quote's outcome never closes the opportunity
	 * it is linked to.
	 *
	 * SM1 exposure (these six columns): none. All are nullable with no default
	 * and the legacy CRM never names them on `order`.
	 */
	@Column('varchar', { name: 'quoteOutcome', nullable: true, length: 8 })
	public quoteOutcome!: QuoteOutcome | null;

	/** PD-17102 — the Lost Reasons list value (the same list as opportunities). */
	@Column('int', { name: 'fk_catalog_value__id_quoteLostReason', nullable: true, unsigned: true })
	public quoteLostReasonId!: number | null;

	@Column('varchar', { name: 'quoteLostNote', nullable: true, length: 1000 })
	public quoteLostNote!: string | null;

	/** PD-17102 — how far above the winning price the quote was, in percent. */
	@Column('decimal', { name: 'quotePriceGapPercent', nullable: true, precision: 7, scale: 2 })
	public quotePriceGapPercent!: string | null;

	@Column('datetime', { name: 'quoteOutcomeDate', nullable: true })
	public quoteOutcomeDate!: Date | null;

	/** PD-17102 — the cart (later the order) a won quote became. */
	@Column('int', { name: 'fk_order__id_quoteConvertedTo', nullable: true, unsigned: true })
	public quoteConvertedToOrderId!: number | null;

	/**
	 * PD-16802 — the ERP number of the quote this order came from, written by
	 * the ERP import (Prophet 21 today). An ERP that converts a quote in place
	 * keeps the number, so a converted order's own `externalId` appears here;
	 * an order copied from a quote names that quote. NULL for an order entered
	 * directly and for every order created in Workd, whose conversions are
	 * recorded on the quote (`quoteConvertedToOrderId`) instead.
	 *
	 * SM1 exposure (this and `quoteDate`): none. Both are nullable with no
	 * default and the legacy CRM never names them on `order`.
	 */
	@Column('varchar', { name: 'sourceQuoteNumber', nullable: true, length: 64 })
	public sourceQuoteNumber!: string | null;

	/**
	 * PD-16802 — when an ERP quote was entered. Prophet 21 rewrites a quote's
	 * order date to the conversion time when it converts it in place, so the
	 * import records the quote's date here while the document is still a
	 * quote and leaves it alone afterwards. NULL for a quote converted before
	 * the import first saw it, and for every Workd-created order.
	 */
	@Column('datetime', { name: 'quoteDate', nullable: true })
	public quoteDate!: Date | null;

	@ManyToOne(() => CatalogValueEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_catalog_value__id_quoteLostReason', referencedColumnName: 'id' }])
	public quoteLostReason!: CatalogValueEntity | null;

	@Column('varchar', { name: 'internalReference', nullable: true, length: 64 })
	public internalReference!: string | null;

	@Column('int', {
		name: 'fk_shipping_method__id',
		nullable: true,
		unsigned: true,
	})
	public fkShippingMethodId!: number | null;

	@Column('json', {
		name: 'appliedCouponCodes',
		nullable: true,
		comment:
			'Array of coupon codes entered against this cart/order. Persisted on the cart so codes survive page reloads and the promo engine sees them on every recompute. Null/empty array = no codes entered.',
	})
	public appliedCouponCodes!: Array<string> | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@OneToMany(() => ClientEntity, (client) => client.fkOrderIdLast2, {})
	public clients!: Array<ClientEntity>;

	@OneToMany(() => ClientEntity, (client) => client.fkOrder, {})
	public clients2!: Array<ClientEntity>;

	@OneToMany(
		() => ClientAddressItemEntity,
		(clientAddressItem) => clientAddressItem.fkOrderIdLast2,
		{},
	)
	public clientAddressItems!: Array<ClientAddressItemEntity>;

	@OneToMany(
		() => ClientItemEntity,
		(clientItem) => clientItem.fkOrderIdLast2,
		{},
	)
	public clientItems!: Array<ClientItemEntity>;
	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkOrder, {})
	public clientItems2!: Array<ClientItemEntity>;

	@OneToMany(
		() => MSmClientOpportunityEntity,
		(mSmClientOpportunity) => mSmClientOpportunity.mSmFkOrder,
		{},
	)
	public mSmClientOpportunities!: Array<MSmClientOpportunityEntity>;

	@OneToMany(
		() => MSmPaymentTransactionOrderEntity,
		(mSmPaymentTransactionOrder) =>
			mSmPaymentTransactionOrder.mSmPaymentFkOrder,
		{},
	)
	public mSmPaymentTransactionOrders!: Array<MSmPaymentTransactionOrderEntity>;

	@ManyToOne(() => UserEntity, (user) => user.submittedOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id_submitter', referencedColumnName: 'id' }])
	public submittedByUser!: UserEntity;

	@ManyToOne(() => PublicUserEntity, (publicUser) => publicUser.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_submitter', referencedColumnName: 'id' },
	])
	public submittedByPublicUser!: PublicUserEntity;

	@ManyToOne(() => CompanyEntity, (company) => company.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => UserEntity, (user) => user.overriddenOrders, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_user__id_override', referencedColumnName: 'id' }])
	public fkUserIdOverride2!: UserEntity;

	@ManyToOne(
		() => MPaymentPaymentMethodEntity,
		(mPaymentPaymentMethod) => mPaymentPaymentMethod.orders,
		{ onDelete: 'NO ACTION', onUpdate: 'NO ACTION' },
	)
	@JoinColumn([
		{
			name: 'm_sm_payment_fk_m_payment_method__id',
			referencedColumnName: 'id',
		},
	])
	public mSmPaymentFkMPaymentMethod!: MPaymentPaymentMethodEntity;

	@ManyToOne(() => TaxEntity, (tax) => tax.orders, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_tax__id', referencedColumnName: 'id' }])
	public fkTax!: TaxEntity;

	@ManyToOne(() => PublicUserEntity, (publicUser) => publicUser.orders2, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_override', referencedColumnName: 'id' },
	])
	public fkPublicUserIdOverride2!: PublicUserEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.orders,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.orders2,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_shipping', referencedColumnName: 'id' },
	])
	public fkClientAddressIdShipping2!: ClientAddressEntity;

	@ManyToOne(() => ClientEntity, (client) => client.orders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	// PD-15849 — the billing account (see `fkClientIdBilling`).
	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_billing', referencedColumnName: 'id' }])
	public fkClientBilling!: ClientEntity | null;

	// PD-15423 — the delivery-destination client for cross-account delivery.
	// No inverse relation on ClientEntity: read via the received-deliveries
	// query path, not from the client side.
	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_delivery', referencedColumnName: 'id' }])
	public fkDeliveryClient!: ClientEntity | null;

	@ManyToOne(() => UserEntity, (user) => user.assignedOrders, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;

	@ManyToOne(
		() => ShippingMethodEntity,
		(shippingMethod) => shippingMethod.orders,
		{
			onDelete: 'SET NULL',
			onUpdate: 'CASCADE',
		},
	)
	@JoinColumn([{ name: 'fk_shipping_method__id', referencedColumnName: 'id' }])
	public shippingMethod!: ShippingMethodEntity;

	@ManyToOne(() => TermsCodeEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_terms_code__id', referencedColumnName: 'id' }])
	public fkTermsCode!: TermsCodeEntity | null;

	@OneToMany(() => OrderFileEntity, (orderFile) => orderFile.fkOrder, {})
	public orderFiles!: Array<OrderFileEntity>;

	@OneToMany(() => OrderDetailEntity, (orderDetail) => orderDetail.order, {})
	public orderDetails!: Array<OrderDetailEntity>;

	@OneToMany(() => OrderNoteEntity, (orderNote) => orderNote.fkOrder, {})
	public orderNotes!: Array<OrderNoteEntity>;

	// Distribution refactor: order ↔ trip_stop linkage.
	@Column('int', {
		name: 'fk_trip_stop__id',
		unsigned: true,
		nullable: true,
	})
	public fkTripStopId!: number | null;

	@ManyToOne(() => TripStopEntity, { onDelete: 'SET NULL' })
	@JoinColumn([{ name: 'fk_trip_stop__id', referencedColumnName: 'id' }])
	public fkTripStop?: TripStopEntity;

	/**
	 * PD-16958 — the stop this order last went out on and came back from
	 * (skipped or not delivered), kept when the run ends and the order goes
	 * back to waiting. The dispatcher's builder reads it to say "Skipped on
	 * 10/10 trip · Store Closed" beside the waiting order. History only:
	 * nothing treats the order as dispatched because of it.
	 */
	@Column('int', {
		name: 'fk_trip_stop__id_missed',
		unsigned: true,
		nullable: true,
	})
	public fkTripStopIdMissed!: number | null;
}
